import { answerGuidance } from "./copilotAnswerGuides"
import { PRODUCT_IMAGE_GROUNDING } from "./copilotProductImages"

export type CopilotBrand = {
  id: string
  name: string
  urlPatterns: string[]
}

export type CopilotProduct = {
  id: string
  name: string
  companyId: string
  companyName: string
}

export type CopilotBrandScope = {
  unrestricted: boolean
  brands: CopilotBrand[]
  deniedUrlPatterns: string[]
  deniedBrandNames: string[]
  products: CopilotProduct[]
  productsRestricted?: boolean
}

export const BRAND_ACCESS_DENIED =
  "You can only view SharePoint documents for your assigned brands. This answer used documents outside that access, so it was withheld."

function availableProductContext(products: CopilotProduct[]) {
  const byBrand = new Map<string, string[]>()
  products.forEach((product) => {
    const brand = product.companyName || "Other"
    const names = byBrand.get(brand) || []
    if (!names.includes(product.name)) names.push(product.name)
    byBrand.set(brand, names)
  })
  if (!byBrand.size) return "none"
  return [...byBrand.entries()]
    .map(([brand, names]) => `${brand}: ${names.join(", ")}`)
    .join("; ")
}

const SHAREPOINT_GROUNDING = [
  "SharePoint grounding: answer only from SharePoint documents that directly address the question.",
  "Explain what each document says and how it applies. Include the practical recommendation, the reason, and any limit stated in the document.",
  "When the question asks which product to offer, name the model, why it fits the stated use, and which document supports that.",
  "Put the supporting document name in the same sentence or table row as each factual claim.",
  "Do not add a follow-up claim the cited documents do not state. Say that detail is not in those documents.",
  "Copy each SharePoint link exactly. Do not shorten a filename, drop parentheses, or change & in a folder name.",
  "Use one link per document. Do not add a second link for the same file.",
  "Do not cite a website or any link that is not an https SharePoint document.",
  "Do not add a reason of your own. If the cited document does not state it, leave it out.",
  "If no relevant SharePoint document supports the answer, say so and do not invent a source.",
]

export function scopeCopilotQuestion(
  text: string,
  scope: CopilotBrandScope | null,
  options?: {
    imageAttached?: boolean
    documentAttached?: boolean
    productImageRequest?: boolean
    /** Off for the chat's own follow-up turns, which carry their own instructions. */
    answerGuides?: boolean
  }
) {
  if (options?.imageAttached || options?.documentAttached) return text
  const grounding = [
    ...SHAREPOINT_GROUNDING,
    ...(options?.productImageRequest ? PRODUCT_IMAGE_GROUNDING : []),
    ...(options?.answerGuides === false ? [] : answerGuidance(text)),
  ].join("\n")
  if (!scope || scope.unrestricted) return [grounding, "", text].join("\n")
  const names = scope.brands.map((brand) => brand.name).filter(Boolean)
  const allowed = names.length ? names.join(", ") : "none"
  const productLines =
    scope.productsRestricted === false
      ? []
      : [
          `Available products: answer only about these products: ${availableProductContext(scope.products || [])}.`,
          "Do not describe, compare, recommend, or cite any product that is not in that list.",
          "If the question needs a product outside this list, say the user does not have access to that product.",
        ]
  return [
    `Access scope: answer only from SharePoint documents for these assigned brands: ${allowed}.`,
    "Do not open, quote, summarize, or cite documents for any other brand.",
    "If the question needs a brand outside this list, say the user does not have access to that brand.",
    ...productLines,
    grounding,
    "",
    text,
  ].join("\n")
}

export function copilotBrandActivityValue(scope: CopilotBrandScope | null) {
  if (!scope || scope.unrestricted) return { unrestricted: true }
  return {
    unrestricted: false,
    allowedBrands: scope.brands.map((brand) => brand.name),
    allowedUrlPatterns: scope.brands.flatMap((brand) => brand.urlPatterns),
    productsRestricted: scope.productsRestricted !== false,
    allowedProducts:
      scope.productsRestricted === false
        ? []
        : (scope.products || []).map((product) => ({
            name: product.name,
            brand: product.companyName,
          })),
  }
}

function decoded(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function includesPattern(url: string, pattern: string) {
  const needle = decoded(pattern).replace(/\+/g, " ").toLowerCase()
  if (!needle) return false
  return decoded(url).toLowerCase().includes(needle)
}

function nameMatches(name: string, text: string) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, "i").test(text)
}

export function citationIsAllowed(
  citation: { title: string; url?: string },
  scope: CopilotBrandScope | null
) {
  if (!scope || scope.unrestricted) return true
  const url = citation.url || ""
  if (url && scope.deniedUrlPatterns.some((pattern) => includesPattern(url, pattern))) {
    return false
  }
  if (!url && scope.deniedBrandNames.some((name) => nameMatches(name, citation.title))) {
    return false
  }
  // A super admin with one brand closed can still cite every other SharePoint file.
  if (scope.productsRestricted === false) return true
  const allowedPatterns = scope.brands.flatMap((brand) => brand.urlPatterns)
  if (url && /sharepoint\.com/i.test(url) && allowedPatterns.length) {
    return allowedPatterns.some((pattern) => includesPattern(url, pattern))
  }
  if (url && /sharepoint\.com/i.test(url) && !allowedPatterns.length) return false
  return true
}

export function restrictCopilotReply<T extends { text: string; citations: { title: string; url?: string }[] }>(
  reply: T,
  scope: CopilotBrandScope | null
): T {
  if (!scope || scope.unrestricted) return reply
  const citations = reply.citations.filter((citation) => citationIsAllowed(citation, scope))
  const droppedCitation = citations.length !== reply.citations.length
  const mentionsDeniedBrand = scope.deniedBrandNames.some((name) => nameMatches(name, reply.text))
  const mentionsAllowedBrand = scope.brands.some((brand) => nameMatches(brand.name, reply.text))
  if (droppedCitation || (mentionsDeniedBrand && !mentionsAllowedBrand)) {
    return { ...reply, text: BRAND_ACCESS_DENIED, citations: [] }
  }
  return { ...reply, citations }
}
